import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@eriko/ui/src/components/ui/button';
import type { ChatMessage } from '../../../stores/chatStore';
import { useChatStore } from '../../../stores/chatStore';
import { useChat } from '../../../hooks/useChat';
import { AssistantMessage } from './AssistantMessage';
import { ForkNote } from './ForkNote';
import { loadEntityDetail } from './loadEntityDetail';
import { ReviewerMessage } from './ReviewerMessage';
import { SystemMessage } from './SystemMessage';
import { UserMessage } from './UserMessage';

interface ChatMessagesProps {
  messages: ChatMessage[];
  status: string | null;
}

export function ChatMessages({ messages, status }: ChatMessagesProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const artifacts = useChatStore((s) => s.artifacts);
  const setActiveArtifact = useChatStore((s) => s.setActiveArtifact);
  const error = useChatStore((s) => s.error);
  const storeMessages = useChatStore((s) => s.messages);
  const { sendMessage, isStreaming } = useChat();

  const handleAutoSubmit = useCallback(() => {
    if (isStreaming) return;
    void sendMessage('', undefined);
  }, [isStreaming, sendMessage]);

  const handleRetry = () => {
    const lastUser = [...storeMessages]
      .reverse()
      .find((m) => m.senderType !== 'AI' && m.role !== 'assistant');
    if (!lastUser) return;
    sendMessage(lastUser.content);
  };

  const markdownComponents = useMemo(
    () => ({
      a({ href, children }: { href?: string; children?: ReactNode }) {
        if (href?.startsWith('#entity-')) {
          const entityId = href.replace('#entity-', '');
          return (
            <button
              type="button"
              className="text-info hover:text-info/80 underline font-medium cursor-pointer bg-transparent border-none p-0 inline"
              onClick={() => loadEntityDetail(entityId)}
            >
              {children}
            </button>
          );
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info hover:underline"
          >
            {children}
          </a>
        );
      },
    }),
    [],
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, status]);

  const assistantDisplayIndices = useMemo(() => {
    let idx = -1;
    return messages.map((msg) => {
      if (msg.role !== 'system' && !msg.forkNote) idx++;
      return msg.role === 'assistant' ? idx : -1;
    });
  }, [messages]);

  const lastAssistantId = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'assistant')?.id,
    [messages],
  );

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-7 py-5 bg-transparent">
      <div className="flex flex-col gap-4">
        {messages.map((msg, i) => {
          if (msg.role === 'system') return <SystemMessage key={msg.id} msg={msg} />;

          if (msg.forkNote) return <ForkNote key={msg.id} msg={msg} />;

          // Reviewer messages render as a distinct bubble.
          // Check BEFORE the user-role fallback because mapMessage maps REVIEWER
          // senderType to role='user' (since senderType !== 'AI').
          if (msg.senderType === 'REVIEWER') {
            return <ReviewerMessage key={msg.id} msg={msg} />;
          }

          if (msg.role === 'user') return <UserMessage key={msg.id} msg={msg} />;

          const currentIndex = assistantDisplayIndices[i]!;
          const matchingArtifactIndices = artifacts
            .map((a, ai) => (a.messageIndex === currentIndex ? ai : -1))
            .filter((ai) => ai >= 0);

          return (
            <AssistantMessage
              key={msg.id}
              msg={msg}
              status={msg.streaming ? status : null}
              artifactIndices={matchingArtifactIndices}
              artifacts={artifacts}
              onViewArtifact={setActiveArtifact}
              mdComponents={markdownComponents}
              isLatestAssistant={msg.id === lastAssistantId}
              onAutoSubmit={handleAutoSubmit}
            />
          );
        })}
        {error === 'RESUME_TIMEOUT' && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <span>Eriko's reply didn't arrive. The run may have failed — please try again.</span>
            <Button size="sm" variant="outline" onClick={handleRetry}>
              <RefreshCw className="w-3 h-3" /> Retry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
